import { getSegmentsTrack } from '@goldcast/player-config/src/utils/playerConfigGetters';
import { Segment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/Segment';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { getClipFPS, isSplitsInTimelineEnabledPure } from '@goldcast/player-config/src/utils/clipUtils';
import { approxLT, approxLTE } from '@goldcast/player-config/src/utils/compareUtils';
import {
  getAbsolutePlayerTimePure,
  getDeletesForSegmentPure,
  getSegmentPlayerTimeFromRemainingAbsoluteTime
} from '@goldcast/player-config/src/utils/timeUtils';
import { zoomLevelStore } from './store/zoomLevel';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

// Minimum width for intro, outro segment
export const MIN_SEGMENT_WIDTH = 24;

// Minimum duration for a video segment in seconds
export const MIN_VIDEO_SEGMENT_DURATION = 3;

export const getSegmentPlayerTimeFromAbsoluteTime = (
  absoluteTime: number,
  segment: Segment,
  clipData: Clip,
  minSegmentWidth = MIN_SEGMENT_WIDTH
) => {
  const remainingAbsoluteTime = absoluteTime - getPreviousSegmentAbsoluteTime(segment, clipData);
  return getSegmentPlayerTimeFromRemainingAbsoluteTime({
    remainingAbsoluteTime,
    segment,
    clipData,
    minSegmentWidth,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations],
    zoomLevel: zoomLevelStore.getSnapshot().zoomLevel
  });
};

/**
 * Calculates the total absolute time of all segments that come before the specified segment.
 * This function iterates through all segments in the player configuration and sums up
 * the duration of segments that appear before the target segment in the timeline.
 *
 * @param segment - The target segment to calculate previous segments' time for
 * @param clipData - The clip data containing metadata and player configuration
 * @param minSegmentWidth - The minimum width (in pixels) that a segment should have in the timeline view.
 *                          Passing -Infinity disables the minimum segment width constraint, allowing segments
 *                          to have their actual calculated width without any minimum size enforcement.
 * @returns The total absolute time (in seconds) of all segments before the target segment
 */
export const getPreviousSegmentAbsoluteTime = (
  segment: Segment,
  clipData: Clip,
  minSegmentWidth = MIN_SEGMENT_WIDTH
) => {
  const playerConfig = clipData.asset_metadata.player_config;

  if (!playerConfig) {
    return 0;
  }
  const fps = getClipFPS(clipData);

  const segmentTrack = getSegmentsTrack(playerConfig);
  let previousSegmentTime = 0;
  const zoomLevel = zoomLevelStore.getSnapshot().zoomLevel;
  const sortedSegments = [...segmentTrack.segments].sort((a, b) => a.fromFrame - b.fromFrame);
  for (const currentSegment of sortedSegments) {
    if (currentSegment.id === segment.id) {
      return previousSegmentTime;
    }
    if (currentSegment.type === SegmentType.COMPOSITE) {
      previousSegmentTime += (currentSegment.currentBounds.toFrame - currentSegment.currentBounds.fromFrame) / fps;
    } else {
      previousSegmentTime += Math.max(
        (currentSegment.toFrame - currentSegment.fromFrame) / fps,
        minSegmentWidth / fps / zoomLevel
      );
    }
  }
  return previousSegmentTime;
};

/**
 * Calculates the total time of all segments that come before the specified segment.
 * This function iterates through all segments in the player configuration and sums up
 * the duration of segments that appear before the target segment in the timeline.
 *
 * @param segment - The target segment to calculate previous segments' time for
 * @param clipData - The clip data containing metadata and player configuration
 * @returns The total time of all segments before the target segment
 */
export const getPreviousSegmentTime = (segment: Segment, clipData: Clip) => {
  const playerConfig = clipData.asset_metadata.player_config;

  if (!playerConfig) {
    return 0;
  }
  const fps = getClipFPS(clipData);

  const segmentTrack = getSegmentsTrack(playerConfig);
  let previousSegmentTime = 0;
  segmentTrack.segments.some(currentSegment => {
    if (currentSegment.id === segment.id) {
      return true;
    }
    previousSegmentTime += (currentSegment.toFrame - currentSegment.fromFrame) / fps;
    return false;
  });
  return previousSegmentTime;
};

/**
 * Finds the segment that contains the current frame, based on the current time and clip data.
 *
 * @param {number} currentTime - The current time in seconds (player time).
 * @param {Clip} clipData - The clip data containing metadata and player configuration.
 * @returns {Segment | null} The segment at the current frame, or null if none is found or player config is missing.
 */
export const getSegmentAtCurrentFrame = (currentTime: number, clipData: Clip) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return null;
  }
  const fps = getClipFPS(clipData);
  const frame = currentTime * fps;

  const segmentsTrack = getSegmentsTrack(playerConfig);
  const segment = segmentsTrack.segments.find(
    segment => approxLTE(segment.fromFrame, frame) && approxLT(frame, segment.toFrame)
  );
  return segment || null;
};

export const getSegmentTimeFromPlayerTime = (playerTime: number, clipData: Clip, segmentId: string) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return 0;
  }
  const segmentTrack = getSegmentsTrack(playerConfig);
  const segment = segmentTrack.segments.find(segment => segment.id === segmentId);
  if (!segment) {
    return 0;
  }

  const fps = getClipFPS(clipData);

  const absolutePlayerTime = getAbsolutePlayerTimePure({
    time: playerTime,
    clipData,
    minSegmentWidth: -Infinity,
    zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });
  const previousSegmentAbsoluteTime = getPreviousSegmentAbsoluteTime(segment, clipData, -Infinity);
  const isSplitsEnabled = isSplitsInTimelineEnabledPure({
    clip: clipData,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });
  if ((segment.type === SegmentType.VIDEO && isSplitsEnabled) || segment.type === SegmentType.COMPOSITE) {
    return absolutePlayerTime - previousSegmentAbsoluteTime + (segment.currentBounds?.fromFrame || 0) / fps;
  }
  return absolutePlayerTime - previousSegmentAbsoluteTime;
};

export const getPlayerTimeFromSegmentTime = (segmentTime: number, clipData: Clip, segmentId: string) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return 0;
  }
  const segmentTrack = getSegmentsTrack(playerConfig);
  const segment = segmentTrack.segments.find(segment => segment.id === segmentId);
  if (!segment) {
    return 0;
  }
  const fps = getClipFPS(clipData);

  const isSplitsEnabled = isSplitsInTimelineEnabledPure({
    clip: clipData,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });

  if (
    (!isSplitsEnabled && segment.type === SegmentType.VIDEO) ||
    (segment.type !== SegmentType.COMPOSITE && segment.type !== SegmentType.VIDEO)
  ) {
    return segment.fromFrame / fps + segmentTime;
  }
  const startTime = segment.fromFrame / fps;
  const allDeletes = getDeletesForSegmentPure({
    clip: clipData,
    segmentId: segment.id,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });
  let deletedTime = 0;
  for (const [_, range] of Object.entries(allDeletes)) {
    if (approxLTE(range.bounds[1], segmentTime)) {
      deletedTime += range.bounds[1] - range.bounds[0];
    } else if (approxLTE(range.bounds[0], segmentTime)) {
      deletedTime += segmentTime - range.bounds[0];
    }
  }
  return startTime + segmentTime - deletedTime;
};

export const getAbsoluteTimeFromSegmentTime = (
  segmentTime: number,
  clipData: Clip,
  segmentId: string,
  minSegmentWidth = MIN_SEGMENT_WIDTH
) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return 0;
  }
  const segmentTrack = getSegmentsTrack(playerConfig);
  const segment = segmentTrack.segments.find(segment => segment.id === segmentId);
  if (!segment) {
    return 0;
  }
  const startAbsoluteTime = getPreviousSegmentAbsoluteTime(segment, clipData, minSegmentWidth);

  const isSplitsEnabled = isSplitsInTimelineEnabledPure({
    clip: clipData,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });

  if (
    (!isSplitsEnabled && segment.type !== SegmentType.COMPOSITE) ||
    (segment.type !== SegmentType.VIDEO && segment.type !== SegmentType.COMPOSITE)
  ) {
    return startAbsoluteTime + segmentTime;
  }
  const fps = getClipFPS(clipData);
  const trimOffset = (segment?.currentBounds?.fromFrame || 0) / fps;
  const segmentTotalTime = ((segment.currentBounds?.toFrame || 0) - (segment.currentBounds?.fromFrame || 0)) / fps;
  if (segment.type === SegmentType.VIDEO) {
    const segmentTotalSize = segmentTotalTime * fps * zoomLevelStore.getSnapshot().zoomLevel;
    if (approxLTE(segmentTotalSize, minSegmentWidth)) {
      return (
        startAbsoluteTime + (Math.min(segmentTime - trimOffset, segmentTotalTime) * minSegmentWidth) / segmentTotalSize
      );
    }
  }
  return startAbsoluteTime + Math.max(0, Math.min(segmentTime - trimOffset, segmentTotalTime));
};

export const getSegmentTimeFromAbsoluteTime = (
  absoluteTime: number,
  clipData: Clip,
  segmentId: string,
  minSegmentWidth = MIN_SEGMENT_WIDTH
) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return 0;
  }
  const segmentTrack = getSegmentsTrack(playerConfig);
  const segment = segmentTrack.segments.find(segment => segment.id === segmentId);
  if (!segment) {
    return 0;
  }
  const fps = getClipFPS(clipData);

  const isSplitsEnabled = isSplitsInTimelineEnabledPure({
    clip: clipData,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });

  if (
    (!isSplitsEnabled && segment.type !== SegmentType.COMPOSITE) ||
    (segment.type !== SegmentType.VIDEO && segment.type !== SegmentType.COMPOSITE)
  ) {
    return absoluteTime - getPreviousSegmentAbsoluteTime(segment, clipData);
  }

  const trimOffset = (segment?.currentBounds?.fromFrame || 0) / fps;
  const segmentTotalAbsoluteTime =
    ((segment.currentBounds?.toFrame || 0) - (segment.currentBounds?.fromFrame || 0)) / fps;

  if (segment.type === SegmentType.VIDEO) {
    const segmentTotalSize = segmentTotalAbsoluteTime * fps * zoomLevelStore.getSnapshot().zoomLevel;
    if (approxLTE(segmentTotalSize, minSegmentWidth)) {
      return (
        Math.min(
          segmentTotalAbsoluteTime,
          (absoluteTime - getPreviousSegmentAbsoluteTime(segment, clipData)) * (segmentTotalSize / minSegmentWidth)
        ) + trimOffset
      );
    }
  }

  return (
    Math.min(segmentTotalAbsoluteTime, absoluteTime - getPreviousSegmentAbsoluteTime(segment, clipData)) + trimOffset
  );
};
